import type { FeatureStateStore } from "../feature-state/FeatureStateStore";
import type { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import type { InteractionBlockReason } from "../interaction-block/InteractionBlockTypes";
import type { GameInitialSettings } from "../boot/settings/GameInitialSettingsTypes";
import type { BarButtonPort } from "../controls/bar/BarButtonPort";
import { SpinCoordinator } from "../spin/SpinCoordinator";
import { AutoSpinStore } from "./AutoSpinStore";
import { isAutospinAvailable } from "./autospinAvailability";
import type { ActiveAutoSpinButtonPort, AutoSpinPopupPort } from "./AutoSpinPorts";
import type { ViewportStore } from "@slot/presentation";

export interface AutoSpinPresenterDeps {
  featureStateStore: FeatureStateStore;
  /** Set by the mobile picker; read at each idle of a run, so no bet is placed behind a page or a modal. */
  interactionBlockStore: InteractionBlockStore;
  /** Operator settings. Autospin exists only when it is enabled AND counts were sent (`isAutospinAvailable`). */
  autospinEnabled: boolean;
  autospinPresets: GameInitialSettings["autospinPresets"];
  viewport: ViewportStore;
  /**
   * A round waiting for the player's Spin stops a run (decision 55): a run never presses that Spin, and its active
   * button stands where the Spin button would show.
   */
  playerSpins?: { readonly waiting: boolean; subscribe(listener: () => void): () => void };
}

/**
 * The blocks a run spins on through: the mobile menu and the two pickers, the bar's own popovers (the
 * speed picker is designed to stay open across rounds). Any other block — a page, a modal, and any
 * reason added later — ends the run at the next idle (user, 2026-09-27).
 */
const RUN_THROUGH_BLOCKS: ReadonlySet<InteractionBlockReason> = new Set<InteractionBlockReason>(["menu", "autospin_popup", "speed_mode_popup"]);

/**
 * The autospin flow: the bar toggle opens the picker (a popover on desktop, a blocking overlay on
 * mobile), a preset selects, Start runs the count through the coordinator one idle at a time, the
 * active button stops it. A bonus game closes the picker and stops a run; so does an idle reached
 * while a page or a modal blocks the game (the mobile menu and the pickers excepted), and a round
 * waiting for the player's Spin (decision 55).
 */
export class AutoSpinPresenter {
  private unsubscribe: Array<() => void> = [];

  public constructor(
    private readonly deps: AutoSpinPresenterDeps,
    private readonly spinCoordinator: SpinCoordinator,
    private readonly store: AutoSpinStore,
    private readonly input: {
      toggleButton: BarButtonPort;
      popup: AutoSpinPopupPort;
      activeButton: ActiveAutoSpinButtonPort;
      onBeforeOpen?: () => void;
    },
  ) {
    this.input.popup.bind({
      onClose: () => this.closePopup(),
      onStart: () => this.handleStart(),
      onSelect: (count) => this.store.setSelectedCount(count),
      onOutsideClick: () => this.closePopup(),
    });
    this.input.activeButton.bind({ onStop: () => this.store.stop() });
    this.input.toggleButton.setOnTap(() => this.handleTogglePopup());
  }

  public start(): void {
    this.input.popup.setOptions(this.resolveOptions());
    this.unsubscribe = [
      // A shape change re-applies the blocking rule while open.
      this.deps.viewport.subscribe(() => {
        if (this.store.getSnapshot().popupOpen) this.applyInteractionBlock();
        this.render();
      }),
      this.store.subscribe(() => this.render()),
      this.spinCoordinator.subscribe(() => this.handleSpinCoordinatorChange()),
      this.deps.featureStateStore.subscribe(() => this.handleFeatureStateChange()),
      ...(this.deps.playerSpins ? [this.deps.playerSpins.subscribe(() => this.handlePlayerSpinWait())] : []),
    ];
    this.render();
  }

  public stop(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe = [];
    this.input.popup.hide();
    this.deps.interactionBlockStore.remove("autospin_popup");
  }

  public render(): void {
    const snapshot = this.store.getSnapshot();
    const bonusRunning = this.isBonusGameRunning();
    const canChangeBet = this.spinCoordinator.getSnapshot().canChangeBet;
    const featureEnabled = this.isAvailable();
    const canUseAutoSpin = featureEnabled && !bonusRunning && canChangeBet;
    const showToggle = !snapshot.running && canUseAutoSpin;
    this.input.toggleButton.setShown(showToggle);
    this.input.toggleButton.setInteractable(showToggle);
    if (snapshot.popupOpen) this.input.popup.show();
    else this.input.popup.hide();
    this.input.popup.setSelected(snapshot.selectedCount);
    this.input.popup.setOptionsInteractable(!snapshot.running && !bonusRunning);
    this.input.popup.setStartInteractable(!snapshot.running && snapshot.selectedCount !== null && canUseAutoSpin);
    this.input.activeButton.setVisible(featureEnabled && snapshot.running);
    this.input.activeButton.setAmount(snapshot.remainingCount.toString());
    this.input.activeButton.setInteractable(canChangeBet || snapshot.running);
  }

  public closeIfOpen(): void {
    if (this.store.getSnapshot().popupOpen) this.closePopup();
  }

  /** The server's counts, as sent. With none, autospin is unavailable and the picker never opens. */
  private resolveOptions(): number[] {
    return [...(this.deps.autospinPresets ?? [])];
  }

  private isDesktop(): boolean {
    return this.deps.viewport.getSnapshot().shape === "desktop";
  }

  private handleTogglePopup(): void {
    const snapshot = this.store.getSnapshot();
    // The picker refuses by itself rather than trusting the hidden toggle: with no counts it has
    // nothing to offer, and it must never open empty (user, 2026-09-23).
    if (!this.isAvailable() || snapshot.running || this.isBonusGameRunning()) return;
    if (snapshot.popupOpen) {
      this.closePopup();
      return;
    }
    this.input.onBeforeOpen?.();
    this.store.openPopup();
    this.applyInteractionBlock();
    this.input.popup.show();
  }

  private closePopup(): void {
    this.store.closePopup();
    this.input.popup.hide();
    this.deps.interactionBlockStore.remove("autospin_popup");
  }

  /** The mobile overlay blocks the game; the desktop popover leaves it live. */
  private applyInteractionBlock(): void {
    if (this.isDesktop()) this.deps.interactionBlockStore.remove("autospin_popup");
    else this.deps.interactionBlockStore.add("autospin_popup");
  }

  private handleStart(): void {
    // Start's own rule (render: `canUseAutoSpin`), not only the element's disabled state: a run the coordinator would refuse never starts (review 2026-09-27 #63).
    if (!this.spinCoordinator.getSnapshot().canChangeBet) return;
    const snapshot = this.store.getSnapshot();
    if (!snapshot.selectedCount || snapshot.running || this.isBonusGameRunning()) return;
    this.deps.interactionBlockStore.remove("autospin_popup");
    this.input.popup.hide();
    this.store.start();
    void this.spinCoordinator.requestSpin();
  }

  private handleSpinCoordinatorChange(): void {
    const spinSnapshot = this.spinCoordinator.getSnapshot();
    const autoSpinSnapshot = this.store.getSnapshot();
    if (autoSpinSnapshot.popupOpen && this.isDesktop() && spinSnapshot.phase !== "idle") this.closePopup();
    if (this.isBonusGameRunning() && autoSpinSnapshot.running) {
      this.store.stop();
      this.render();
      return;
    }
    if (!autoSpinSnapshot.running) {
      this.render();
      return;
    }
    if (spinSnapshot.phase === "idle") {
      // No bet behind a page or a modal (the boot boundary's Reload among them): the run stops here,
      // it does not wait for the block to lift. A block opened and closed within the round is gone by now.
      if (this.deps.interactionBlockStore.getSnapshot().reasons.some((reason) => !RUN_THROUGH_BLOCKS.has(reason))) {
        this.store.stop();
      } else {
        this.store.consumeOne();
        const next = this.store.getSnapshot();
        if (next.running && next.remainingCount > 0 && !this.isBonusGameRunning()) void this.spinCoordinator.requestSpin();
      }
    }
    this.render();
  }

  private handleFeatureStateChange(): void {
    if (this.isBonusGameRunning()) {
      const snapshot = this.store.getSnapshot();
      if (snapshot.popupOpen) this.closePopup();
      if (snapshot.running) this.store.stop();
    }
    this.render();
  }

  private handlePlayerSpinWait(): void {
    if (this.deps.playerSpins?.waiting && this.store.getSnapshot().running) this.store.stop();
  }

  /** Enabled by the operator AND given counts by the server (`isAutospinAvailable`). */
  private isAvailable(): boolean {
    return isAutospinAvailable({ autospinEnabled: this.deps.autospinEnabled, autospinPresets: this.deps.autospinPresets });
  }

  private isBonusGameRunning(): boolean {
    return this.deps.featureStateStore.isFeatureActive();
  }
}
